import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import WelcomeCard from "../WelcomeCard";
import CommonTextField from "../commonTextField/CommonTextField";
import CommonDropdown from "../commonDropDown/CommonDropDown";
import { timezones } from "../../constants/constants";
import CommonInputField from "../CommonInputField/CommonInputField";
import FlexibleTextarea from "../textArea/FlexibleTextarea";
import TransparentButton from "../transparentButton/TransparentButton";
import { api1 } from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { DateTime } from "luxon";

const difficultyLevels = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
];

const durationOptions = [
  { value: 1, label: "1 Week" },
  { value: 2, label: "2 Weeks" },
  { value: 3, label: "3 Weeks" },
  { value: 4, label: "4 Weeks" },
  { value: 6, label: "6 Weeks" },
  { value: 8, label: "8 Weeks" },
  { value: 12, label: "12 Weeks" },
  { value: 16, label: "16 Weeks" },
];

const courseSchema = yup.object().shape({
  courseName: yup.string().required("Course name is required"),
  instructorName: yup.string().required("Instructor name is required"),
  timezone: yup.string().required("Time zone is required"),
  date: yup.string().required("Date is required"),
  duration: yup
    .number()
    .typeError("Duration must be a number")
    .required("Duration is required")
    .min(1, "Duration must be at least 1 week"),
  difficultyLevel: yup
    .string()
    .oneOf(["beginner", "intermediate", "advanced"])
    .required("Difficulty level is required"),
  seats: yup
    .number()
    .typeError("Seats must be a number")
    .required("Seats are required")
    .min(1, "Seats must be at least 1"),
  price: yup
    .number()
    .typeError("Price must be a number")
    .required("Price is required")
    .min(0, "Price cannot be negative"),
  courseDescription: yup.string().required("Course description is required"),
  courseType: yup.string().oneOf(["ontrack", "partnerinstitution"]).required("Course type is required"),
  institutionName: yup
    .string()
    .when("courseType", {
      is: "partnerinstitution",
      then: (schema) => schema.required("Institution name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const CreateCourses = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { courseData } = location.state || {};

  const {
    register,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(courseSchema),
  });

  const courseType = watch("courseType");

  const formatDateForInput = (date, timezone) => {
    if (!date || !timezone) return "";
    return DateTime.fromISO(date).setZone(timezone).toFormat("yyyy-MM-dd'T'HH:mm");
  };

  const onSubmit = async (data) => {
    const dateInUTC = DateTime.fromISO(data.date, { zone: data.timezone }).toUTC().toISO();

    const payload = {
      ...data,
      date: dateInUTC,
    };

    try {
      await api1.post(API_ENDPOINTS.CREATE_COURSE, payload);
      navigate("/dashboard/courses/list");
    } catch (error) {
      console.error("Error creating course: ", error);
    }
  };

  const onUpdate = async (data) => {
    const dateInUTC = DateTime.fromISO(data.date, { zone: data.timezone }).toUTC().toISO();

    const payload = {
      ...data,
      date: dateInUTC,
    };

    try {
      await api1.put(`${API_ENDPOINTS.COURSE}/${courseData._id}`, payload);
      navigate("/dashboard/courses/list");
    } catch (error) {
      console.error("Error updating course:", error);
    }
  };

  useEffect(() => {
    if (courseData) {
      const formattedDate = formatDateForInput(courseData.date, courseData.timezone);
      reset({
        courseName: courseData.courseName || "",
        courseDescription: courseData.courseDescription || "",
        instructorName: courseData.instructorName || "",
        date: formattedDate,
        timezone: courseData.timezone || "",
        seats: courseData.seats || "",
        price: courseData.price || "",
        duration: courseData.duration || "",
        difficultyLevel: courseData.difficultyLevel || "beginner",
        courseType: courseData.courseType || "ontrack",
        institutionName: courseData.institutionName || "",
      });
    } else {
      reset({ courseType: "ontrack", difficultyLevel: "beginner" });
    }
  }, [courseData, reset]);

  return (
    <div className="flex flex-col gap-6">
      <WelcomeCard title={courseData ? "Update Course" : "Create Course"} />
      <form
        className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-6"
        onSubmit={handleSubmit(courseData ? onUpdate : onSubmit)}
      >
        <div className="flex flex-col">
          <label className="font-medium mb-1">Course Type</label>
          <div className="flex gap-4 items-center">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="ontrack"
                {...register("courseType")}
                defaultChecked
              />
              OnTrack
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                value="partnerinstitution"
                {...register("courseType")}
              />
              Partner Institution
            </label>
          </div>
          {errors.courseType && (
            <span className="text-red-500 text-sm">{errors.courseType.message}</span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CommonTextField
            name="courseName"
            label="Course Name"
            register={register}
            errors={errors}
            placeholder="Enter Course Name"
          />
          <CommonTextField
            name="instructorName"
            label="Instructor Name"
            register={register}
            errors={errors}
            placeholder="Enter Instructor Name"
          />
          <CommonDropdown
            label="Time zone"
            name="timezone"
            register={register}
            errors={errors}
            search={true}
            options={timezones}
            defaultValue={watch("timezone")}
          />
          <CommonInputField
            name="date"
            label="Date"
            type="datetime-local"
            defaultValue={watch("date")}
            register={register}
            errors={errors}
          />
          <CommonDropdown
            label="Duration (Weeks)"
            name="duration"
            register={register}
            errors={errors}
            options={durationOptions}
            defaultValue={watch("duration")}
          />
          <CommonDropdown
            label="Difficulty Level"
            name="difficultyLevel"
            register={register}
            errors={errors}
            options={difficultyLevels}
            defaultValue={watch("difficultyLevel") || "beginner"}
          />
          <CommonInputField
            name="seats"
            label="Seats"
            type="number"
            register={register}
            errors={errors}
          />
          <CommonInputField
            name="price"
            label="Price ($)"
            type="number"
            register={register}
            errors={errors}
          />

          {courseType === "partnerinstitution" && (
            <CommonTextField
              name="institutionName"
              label="Institution Name"
              register={register}
              errors={errors}
              placeholder="Enter Institution Name"
            />
          )}
        </div>

        <FlexibleTextarea
          label="Course Description"
          name="courseDescription"
          register={register}
          errors={errors}
          placeholder="Write a description..."
        />

        <TransparentButton buttonText={courseData ? "Update Course" : "Create Course"} />
      </form>
    </div>
  );
};

export default CreateCourses;
